import { getCookie } from 'cookies-next'

import { stripLocalePrefix } from '@/utils/route-transition.utils'
import { getSignInRedirectDestination } from '@/utils/tenant.utils'

import { UnInterceptedApiError } from '@/types/api'

/** Matches fsds-be `ErrorCode.Invalid_User`. */
export const API_ERROR_INVALID_USER = 'User is invalid'

const SIGN_IN_PATHS = new Set(['/sign-in', '/en/sign-in'])

const DUPLICATE_KEY_ERROR_RE = /duplicate key value violates unique constraint|unique constraint/i
const USER_ALREADY_EXISTS_MESSAGE = 'User already exists'

/**
 * NestJS nested class-validator errors often look like:
 * `courseModules.0.lessons.0.courseMaterials.0.quiz.question.0.Each quiz question must have…`
 * Strip the dotted path so the toast/form can show the human sentence.
 */
function stripNestedValidationPathPrefix(message: string): string {
  const trimmed = message.trim()
  const match = trimmed.match(/^(?:(?:[A-Za-z_][\w]*)(?:\.\d+)?\.)+([A-Z].*)$/)
  return match?.[1] ?? trimmed
}

export function normalizeApiErrorMessage(message: unknown): string {
  if (typeof message === 'string') {
    if (DUPLICATE_KEY_ERROR_RE.test(message)) {
      return USER_ALREADY_EXISTS_MESSAGE
    }
    return stripNestedValidationPathPrefix(message)
  }

  if (Array.isArray(message)) {
    return message
      .map(item => normalizeApiErrorMessage(item))
      .filter(Boolean)
      .join(', ')
  }

  if (message && typeof message === 'object') {
    return Object.values(message as Record<string, unknown>)
      .map(item => normalizeApiErrorMessage(item))
      .filter(Boolean)
      .join(', ')
  }

  return ''
}

export function getApiErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return ''
  }

  const response = (error as { response?: { data?: UnInterceptedApiError } }).response
  return normalizeApiErrorMessage(response?.data?.message)
}

export function isInvalidUserApiError(error: unknown): boolean {
  return getApiErrorMessage(error) === API_ERROR_INVALID_USER
}

export function isSignInPath(pathname: string): boolean {
  if (SIGN_IN_PATHS.has(pathname)) {
    return true
  }

  return pathname.startsWith('/sign-in/') || pathname.startsWith('/en/sign-in/')
}

/** Routes that must stay reachable while logged out (no sign-in redirect on invalid session). */
export function isPublicAuthPath(pathname: string): boolean {
  if (isSignInPath(pathname)) {
    return true
  }

  if (pathname === '/sign-up' || pathname === '/en/sign-up' || pathname === '/super-admin/sign-in') {
    return true
  }

  return pathname.startsWith('/auth/') || pathname.startsWith('/en/auth/')
}

/** Paths that require a valid session; invalid-user API errors trigger sign-in redirect. */
const PROTECTED_PATH_PREFIXES = [
  '/admin',
  '/my-courses',
  '/my-profile',
  '/setting',
  '/survey',
  '/online-tests',
] as const

/** Exact paths or patterns that stay public despite matching a protected prefix. */
const PUBLIC_PROTECTED_PATH_EXCEPTIONS: ReadonlyArray<string | RegExp> = [
  '/online-tests',
  /^\/online-tests\/[^/]+$/,
]

function isPublicProtectedPathException(pathname: string): boolean {
  const path = stripLocalePrefix(pathname)
  return PUBLIC_PROTECTED_PATH_EXCEPTIONS.some(
    exception => (typeof exception === 'string' ? path === exception : exception.test(path))
  )
}

export function isProtectedPath(pathname: string): boolean {
  const path = stripLocalePrefix(pathname)
  if (isPublicProtectedPathException(pathname)) {
    return false
  }

  return PROTECTED_PATH_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`)
  )
}

export function getClientSignInRedirectUrl(returnPath?: string): string {
  const locale = getCookie('NEXT_LOCALE') === 'en' ? 'en' : 'vi'
  const path =
    returnPath ??
    (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined)

  return getSignInRedirectDestination(locale, path)
}
